import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { useAddressSuggestions } from "@/hooks/useAddressSuggestions";

interface AddressSuggestion {
  display: string;
  city: string;
  region: string;
  address?: string;
  postalCode?: string;
  lat: number;
  lng: number;
  // Internal fields for precise geocoding
  _needsPreciseGeocode?: boolean;
  _streetName?: string;
  _houseNumber?: string;
}

interface AddressAutocompleteProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  onSuggestionSelect: (suggestion: AddressSuggestion) => void;
  placeholder?: string;
  required?: boolean;
  type: "city" | "address";
  disabled?: boolean;
  filterByCity?: string; // Miasto do filtrowania wyników (dla adresów)
}

// Helper function to fetch postal code via reverse geocoding
const fetchPostalCodeForCoords = async (lat: number, lng: number): Promise<string | null> => {
  try {
    const params = new URLSearchParams({
      lat: lat.toString(),
      lon: lng.toString(),
      format: "json",
      addressdetails: "1",
      zoom: "18", // High zoom = more precise address data
    });
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?${params}`,
      {
        headers: {
          "User-Agent": "GdzieSchronisko-ShelterApp/1.0",
          "Accept-Language": "pl",
        },
      }
    );
    if (response.ok) {
      const data = await response.json();
      return data?.address?.postcode || null;
    }
  } catch {
    // Ignore errors
  }
  return null;
};

// Helper function to geocode a full address (street + house number) for precise coordinates
const geocodeFullAddress = async (
  street: string,
  houseNumber: string,
  city: string
): Promise<{ lat: number; lng: number; postcode?: string } | null> => {
  try {
    const fullAddress = `${street} ${houseNumber}, ${city}, Polska`;
    const params = new URLSearchParams({
      q: fullAddress,
      format: "json",
      limit: "1",
      countrycodes: "pl",
      addressdetails: "1",
    });
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?${params}`,
      {
        headers: {
          "User-Agent": "GdzieSchronisko-ShelterApp/1.0",
          "Accept-Language": "pl",
        },
      }
    );
    if (response.ok) {
      const data = await response.json();
      if (data && data.length > 0) {
        const result = data[0];
        const lat = parseFloat(result.lat);
        const lng = parseFloat(result.lon);
        // Validate coordinates are in Poland
        if (lat >= 49 && lat <= 55 && lng >= 14 && lng <= 24) {
          return {
            lat,
            lng,
            postcode: result.address?.postcode || undefined,
          };
        }
      }
    }
  } catch {
    // Ignore errors, fall back to original coordinates
  }
  return null;
};

export const AddressAutocomplete = ({
  id,
  value,
  onChange,
  onSuggestionSelect,
  placeholder,
  required,
  type,
  disabled,
  filterByCity,
}: AddressAutocompleteProps) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [selectedFromList, setSelectedFromList] = useState(false);
  const [userIsTyping, setUserIsTyping] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const { suggestions, isLoading, fetchSuggestions, clearSuggestions } = useAddressSuggestions();

  // Pobierz sugestie przy zmianie wartości
  useEffect(() => {
    // Only fetch suggestions and show dropdown if user is actively typing
    if (value.length >= 2 && userIsTyping) {
      fetchSuggestions(value, type, filterByCity);
      setShowSuggestions(true);
      setSelectedFromList(false);
    } else if (value.length < 2) {
      clearSuggestions();
      setShowSuggestions(false);
    }
    // Reset typing flag after processing
    setUserIsTyping(false);
  }, [value, type, filterByCity, fetchSuggestions, clearSuggestions, userIsTyping]);

  // Zamknij sugestie po kliknięciu poza komponentem
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserIsTyping(true);
    onChange(e.target.value);
    setHighlightedIndex(-1);
  };

  const handleSuggestionClick = async (suggestion: AddressSuggestion) => {
    setSelectedFromList(true);
    setShowSuggestions(false);
    clearSuggestions();
    
    let finalSuggestion = { ...suggestion };
    
    // If suggestion needs precise geocoding (has house number), do it now
    if (suggestion._needsPreciseGeocode && suggestion._streetName && suggestion._houseNumber && suggestion.city) {
      const preciseCoords = await geocodeFullAddress(
        suggestion._streetName,
        suggestion._houseNumber,
        suggestion.city
      );
      if (preciseCoords) {
        finalSuggestion = {
          ...finalSuggestion,
          lat: preciseCoords.lat,
          lng: preciseCoords.lng,
          postalCode: preciseCoords.postcode || finalSuggestion.postalCode,
        };
      }
    }
    
    // If still no postal code, try to fetch it via reverse geocode
    if (!finalSuggestion.postalCode && finalSuggestion.lat && finalSuggestion.lng) {
      const postalCode = await fetchPostalCodeForCoords(finalSuggestion.lat, finalSuggestion.lng);
      if (postalCode) {
        finalSuggestion = { ...finalSuggestion, postalCode };
      }
    }
    
    // Clean up internal fields before passing to parent
    const { _needsPreciseGeocode, _streetName, _houseNumber, ...cleanSuggestion } = finalSuggestion;
    onSuggestionSelect(cleanSuggestion);
  };

  // Auto-select first suggestion on blur if user didn't select from list
  const handleBlur = () => {
    setTimeout(async () => {
      if (!selectedFromList && userIsTyping === false && suggestions.length > 0 && value.length >= 2 && showSuggestions) {
        // Auto-select the first matching suggestion
        const firstSuggestion = suggestions[0];
        if (firstSuggestion) {
          let suggestionToUse = { ...firstSuggestion };
          
          // If suggestion needs precise geocoding (has house number), do it now
          if (firstSuggestion._needsPreciseGeocode && firstSuggestion._streetName && firstSuggestion._houseNumber && firstSuggestion.city) {
            const preciseCoords = await geocodeFullAddress(
              firstSuggestion._streetName,
              firstSuggestion._houseNumber,
              firstSuggestion.city
            );
            if (preciseCoords) {
              suggestionToUse = {
                ...suggestionToUse,
                lat: preciseCoords.lat,
                lng: preciseCoords.lng,
                postalCode: preciseCoords.postcode || suggestionToUse.postalCode,
              };
            }
          }
          
          // If still no postal code, try to fetch it
          if (!suggestionToUse.postalCode && suggestionToUse.lat && suggestionToUse.lng) {
            const postalCode = await fetchPostalCodeForCoords(suggestionToUse.lat, suggestionToUse.lng);
            if (postalCode) {
              suggestionToUse = { ...suggestionToUse, postalCode };
            }
          }
          
          // Clean up internal fields before passing to parent
          const { _needsPreciseGeocode, _streetName, _houseNumber, ...cleanSuggestion } = suggestionToUse;
          onSuggestionSelect(cleanSuggestion);
          setSelectedFromList(true);
        }
      }
      setShowSuggestions(false);
    }, 200);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[highlightedIndex]);
        } else if (suggestions.length > 0) {
          // If no item is highlighted but there are suggestions, select the first one
          handleSuggestionClick(suggestions[0]);
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        break;
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Input
          ref={inputRef}
          id={id}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onFocus={() => {
            if (suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          autoComplete="off"
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-auto">
          {suggestions.map((suggestion, index) => (
            <button
              key={`${suggestion.display}-${index}`}
              type="button"
              className={`w-full px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground transition-colors ${
                index === highlightedIndex ? "bg-accent text-accent-foreground" : ""
              }`}
              onClick={() => handleSuggestionClick(suggestion)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {suggestion.display}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
